/* eslint-disable @next/next/no-img-element */
'use client'
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Stack, styled } from '@mui/material'
import { motion } from 'framer-motion'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import 'swiper/css/zoom'
import { FreeMode, Navigation, Thumbs, Zoom } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
const StyledBtnArrow = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  width: 32,
  fontSize: '12px',
  aspectRatio: '1/1',
  bgcolor: 'transparent',
  color: theme.palette.primary.contrastText,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '999px',
  '&:hover:not(.swiper-button-disabled)': { backgroundColor: theme.palette.primary.main, color: 'white' },
  '&.swiper-button-disabled': {
    opacity: 0.6,
    cursor: 'default',
    pointerEvent: 'none',
  },
  [theme.breakpoints.up('md')]: {
    width: 40,
    fontSize: '14px',
  },
}))
const StyledSwiperThumbSlide = styled(SwiperSlide)(({ theme }) => ({
  '&.swiper-slide-thumb-active .thumb-item': {
    borderColor: theme.palette.primary.contrastText,
  },
}))
type CommonTypeImage = { src: string; zoomAble: boolean; zoomBasic?: boolean }

//#region Mobile Gallery Modal Component
export const MobileGalleryModal = ({
  images,
  onClose,
  initialIndex = 0,
  isShow,
}: {
  images: CommonTypeImage[]
  onClose: () => void
  initialIndex?: number
  isShow: boolean
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)
  const swiperRef = useRef<SwiperType | null>(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    // Prevent body scroll when modal is open
    if (isShow) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isShow])

  if (!mounted) return null

  const modalContent = (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
      }}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      {/* Close Button */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.2, delay: 0.1 }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 2000,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.1)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Swiper */}
      {images?.length > 0 && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {/* Main Swiper */}
          <Box
            sx={{
              flex: 1,
              width: '100%',
              mb: 2,
              maxHeight: '82vh',
              position: 'relative',
              cursor: 'grab',
            }}
          >
            {/* Navigation Buttons */}
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              gap={1}
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                width: '100%',
                zIndex: 2000,
                transform: 'translate(0, -50%)',
                padding: { xs: '0 4px', md: '0 2rem' },
              }}
            >
              <StyledBtnArrow className="swiper-button-fb-images-prev">
                <ArrowBackIosNewIcon sx={{ color: 'inherit', fontSize: 'inherit' }} />
              </StyledBtnArrow>
              <StyledBtnArrow className="swiper-button-fb-images-next">
                <ArrowBackIosNewIcon
                  sx={{ transform: 'rotate(180deg)', color: 'inherit', fontSize: 'inherit' }}
                />
              </StyledBtnArrow>
            </Stack>
            <Swiper
              modules={[Navigation, Thumbs, Zoom]}
              navigation={{
                enabled: true,
                nextEl: '.swiper-button-fb-images-next',
                prevEl: '.swiper-button-fb-images-prev',
              }}
              thumbs={{ swiper: thumbsSwiper && !thumbsSwiper?.destroyed ? thumbsSwiper : null }}
              zoom={true}
              spaceBetween={20}
              slidesPerView={1}
              initialSlide={initialIndex}
              speed={400}
              onSwiper={(swiper) => {
                swiperRef.current = swiper
              }}
              style={{ height: '100%' }}
            >
              {images.map((image, index) => (
                <SwiperSlide key={`${image}-${index}`}>
                  <Box
                    className="swiper-zoom-container"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Image
                      src={image?.src}
                      alt={`Review image ${index + 1}`}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>

          {/* Thumbnails Swiper */}
          <Box
            sx={{
              height: '60px',
              width: '100%',
              maxWidth: '1200px',
              mx: 'auto',
              px: 2,
            }}
          >
            <Swiper
              modules={[FreeMode, Thumbs]}
              onSwiper={setThumbsSwiper}
              spaceBetween={10}
              slidesPerView="auto"
              freeMode={true}
              watchSlidesProgress={true}
              style={{ height: '100%' }}
            >
              {images?.map((image, index) => (
                <StyledSwiperThumbSlide
                  key={`thumb-${image}-${index}`}
                  style={{ width: 'auto', height: '100%' }}
                >
                  <Box
                    className="thumb-item"
                    sx={{
                      width: '60px',
                      height: 'calc(100% - 4px)',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '2px solid transparent',
                      transition: 'all 0.3s ease',
                      userSelect: 'none',
                      '&:hover': {
                        borderColor: 'white',
                      },
                      '&.swiper-slide-thumb-active': {
                        borderColor: 'white',
                      },
                    }}
                  >
                    <img
                      src={image?.src}
                      alt={`Thumbnail ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </StyledSwiperThumbSlide>
              ))}
            </Swiper>
          </Box>
        </Box>
      )}
    </Box>
  )

  return typeof window !== 'undefined' ? createPortal(modalContent, document.body) : null
}
